import Refresh from "@mui/icons-material/Refresh"
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"
import { DataGridProps } from "@mui/x-data-grid"
import { TFunction } from "i18next"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { UserThingTypeMrn } from "shared/userData"
import { v4 as uuidv4 } from "uuid"
import { useAppDispatch } from "../app/hooks"
import { useGetAdministratorsQuery } from "../features/things/thingsSaApi"
import SaLayout from "./SaLayout"
import SaUserDrawer from "./SaUserDrawer"
import { Sl2Btn } from "./Sl2Btn"
import Sl2DataGrid from "./Sl2DataGrid"

const defaultColWidth = 200

const pageSize = 100

const columns = (t: TFunction): DataGridProps["columns"] => [
  { field: "firstName", headerName: t("firstName"), width: defaultColWidth },
  { field: "lastName", headerName: t("lastName"), width: defaultColWidth },
  {
    field: "email",
    headerName: `✉️ ${t("email")}`,
    width: defaultColWidth,
    renderCell: (params) => {
      return (
        <a href={`mailto:${params.value}`} target="_blank" rel="noreferrer">
          {params.value}
        </a>
      )
    },
  },
  {
    field: "status",
    headerName: t("status"),
    minWidth: defaultColWidth,
    renderCell: (params) => {
      return (
        <Chip
          label={params.value}
          variant="outlined"
          size="small"
          color={params.value === "active" ? "primary" : "warning"}
        />
      )
    },
  },
  {
    field: "externalId",
    headerName: t("externalId"),
    minWidth: defaultColWidth,
  },
  {
    field: "isSuperAdmin",
    headerName: `🔐 ${t("isSuperAdmin")}`,
    minWidth: defaultColWidth,
  },
  {
    field: "createdAt",
    headerName: `📅 ${t("createdAt")}`,
    minWidth: defaultColWidth,
  },
  {
    field: "updatedAt",
    headerName: `📅 ${t("updatedAt")}`,
    minWidth: defaultColWidth,
  },
]

export const SaAdministratorsPage: FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const dispatch = useAppDispatch()

  const [nextToken, setNextToken] = useState<string | undefined>(undefined)

  const q = useGetAdministratorsQuery({
    params: {
      "ap.limit": pageSize + 1,
      "ap.nextToken": nextToken,
    },
  })

  const usersWithUUID = q.data?.things
    ?.filter((d) => d?.thingTypeMrn === UserThingTypeMrn)
    ?.map((user) => ({
      ...user,
      frontendId: uuidv4(),
    }))
  const onRefresh = useCallback(() => {
    if (q.isLoading) {
      return
    }
    setNextToken(undefined)
    q.refetch()
  }, [q.isLoading, q.refetch])

  const [createUserDrawerOpen, setCreateUserDrawerOpen] = useState(false)

  return (
    <SaLayout selectedMenuItemLabelKey="administrators">
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 5 },
          width: "100%",
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          flexWrap="wrap"
          gap={2}
          sx={{ mb: 4 }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "primary.main",
              }}
            >
              {t("adminApp")}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="baseline">
              <Typography
                variant="h4"
                sx={{
                  mt: 0.5,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  color: "text.primary",
                  lineHeight: 1.15,
                }}
              >
                {t("administrators")}
              </Typography>
              {typeof q.data?.meta?.count === "number" && (
                <Typography
                  sx={{
                    color: "text.secondary",
                    fontSize: "1.1rem",
                    fontWeight: 500,
                  }}
                >
                  {q.data.meta.count}
                </Typography>
              )}
            </Stack>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {q.isLoading ? (
              <CircularProgress size={22} />
            ) : (
              <IconButton
                onClick={onRefresh}
                size="small"
                sx={{
                  bgcolor: "rgba(35, 66, 133, 0.06)",
                  color: "primary.main",
                  "&:hover": { bgcolor: "rgba(35, 66, 133, 0.12)" },
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            )}
            <Sl2Btn
              variant="contained"
              disableElevation
              onClick={() => setCreateUserDrawerOpen(true)}
            >
              {t("createAdministrator")}
            </Sl2Btn>
          </Stack>
        </Stack>

        {/* Data grid card */}
        <Box
          sx={{
            bgcolor: "white",
            border: "1px solid",
            borderColor: "rgba(0, 0, 0, 0.08)",
            borderRadius: 2,
            boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
            overflow: "hidden",
          }}
        >
          <Sl2DataGrid
            datagridProps={{
              columns: columns(t),
              rows: usersWithUUID,
              getRowId: (row) => row.frontendId,
              onRowClick: (params) =>
                navigate(`/sa/administrator/${params.row.id}`),
            }}
            isLoading={q.isLoading}
            listCount={q.data?.meta?.count || 0}
            pageSize={pageSize}
            onNextTokenChange={() =>
              setNextToken(JSON.stringify(q.data?.meta?.nextToken))
            }
          />
        </Box>
        {createUserDrawerOpen && (
          <SaUserDrawer
            open={true}
            onClose={() => setCreateUserDrawerOpen(false)}
            onSave={() => {
              setCreateUserDrawerOpen(false)
              q.refetch()
            }}
            onError={(error) => {
              console.error("Error creating administrator:", error)
            }}
            superAdmin={true}
          />
        )}
      </Box>
    </SaLayout>
  )
}

export default SaAdministratorsPage
